import { normalizeComparableText, type RawDataColumn, type TurfRespondent } from './adapter';
import { calcReach, type TurfReachResult } from './algorithm';
import { type GlobalDemographicFilter, type QuotaGroup } from './filters';

export interface ComparisonDimension {
  id: string;
  label: string;
  overallReach: TurfReachResult;
  rows: ComparisonRow[];
}

export interface ComparisonKeyFinding {
  dimensionId: string;
  dimensionLabel: string;
  n: number;
  overIndexPct: number;
  reachPct: number;
  rowId: string;
  rowLabel: string;
}

export interface ComparisonRow {
  id: string;
  label: string;
  lowN: boolean;
  n: number;
  overIndexPct: number;
  reach: TurfReachResult;
}

interface BuildComparisonDimensionsOptions {
  basketItemIds: string[];
  columns?: RawDataColumn[];
  demographicFilters?: GlobalDemographicFilter[];
  quotaGroups?: QuotaGroup[];
  respondents?: TurfRespondent[];
}

interface BuildKeyFindingsOptions {
  dimensions?: ComparisonDimension[];
  limit?: number;
  minN?: number;
  minOverIndexPct?: number;
}

interface ComparisonDimensionDefinition {
  accessor: string;
  id: string;
  label: string;
  type?: 'demographic' | 'quota';
}

interface DerivedComparisonDimensionDefinition {
  accessor: string;
  baseDimensionId: string;
  groups: DerivedComparisonGroupDefinition[];
  id: string;
  label: string;
  resolveGroup: (value: unknown) => DerivedComparisonGroupDefinition | undefined;
}

interface DerivedComparisonGroupDefinition {
  id: string;
  label: string;
}

interface RespondentCut {
  label: string;
  respondents: TurfRespondent[];
}

interface SortableComparisonRow extends ComparisonRow {
  sourceIndex: number;
}

const LOW_N_THRESHOLD = 30;
const MIN_KEY_FINDING_N = 30;
const MIN_KEY_FINDING_OVER_INDEX = 0.05;

const COMPARISON_DIMENSION_DEFINITIONS: ComparisonDimensionDefinition[] = [
  {
    accessor: 'quota_group_name',
    id: 'quota_group',
    label: 'Quota Group',
    type: 'quota',
  },
  {
    accessor: 'age_bracket',
    id: 'age',
    label: 'Age',
  },
  {
    accessor: 'age_generation',
    id: 'generation',
    label: 'Generation',
  },
  {
    accessor: 'ethnicity',
    id: 'ethnicity',
    label: 'Ethnicity',
  },
  {
    accessor: 'gender',
    id: 'gender',
    label: 'Gender',
  },
  {
    accessor: 'has_children',
    id: 'has_children',
    label: 'Has Children',
  },
  {
    accessor: 'hh_size',
    id: 'hh_size',
    label: 'Household Size',
  },
  {
    accessor: 'income',
    id: 'income',
    label: 'Income',
  },
  {
    accessor: 'urbanicity',
    id: 'urbanicity',
    label: 'Urbanicity',
  },
  {
    accessor: 'region',
    id: 'region',
    label: 'Region',
  },
];

const AGE_GROUPS: DerivedComparisonGroupDefinition[] = [
  { id: '18-34', label: '18-34' },
  { id: '35-54', label: '35-54' },
  { id: '55+', label: '55+' },
];

const INCOME_GROUPS: DerivedComparisonGroupDefinition[] = [
  { id: 'low_income', label: 'Low Income (Under $40K)' },
  { id: 'middle_income', label: 'Middle Income ($40-125K)' },
  { id: 'high_income', label: 'High Income ($125K)' },
];

const DERIVED_COMPARISON_DIMENSION_DEFINITIONS: DerivedComparisonDimensionDefinition[] = [
  {
    accessor: 'age_bracket',
    baseDimensionId: 'age',
    groups: AGE_GROUPS,
    id: 'age_grouped',
    label: 'Age Grouped',
    resolveGroup: resolveAgeGroup,
  },
  {
    accessor: 'income',
    baseDimensionId: 'income',
    groups: INCOME_GROUPS,
    id: 'income_grouped',
    label: 'Income Grouped',
    resolveGroup: resolveIncomeGroup,
  },
];

export const buildComparisonDimensions = ({
  basketItemIds,
  columns = [],
  demographicFilters = [],
  quotaGroups = [],
  respondents = [],
}: BuildComparisonDimensionsOptions): ComparisonDimension[] => {
  if (!basketItemIds.length || !respondents.length) return [];

  const rawAccessors = new Set(columns.map(({ accessor }) => String(accessor ?? '')));
  const overallReach = calcReach(respondents, basketItemIds);

  return COMPARISON_DIMENSION_DEFINITIONS.filter(({ accessor }) => rawAccessors.has(accessor))
    .flatMap((dimension) => {
      const baseDimension = buildRawComparisonDimension({
        basketItemIds,
        demographicFilters,
        dimension,
        overallReach,
        quotaGroups,
        respondents,
      });
      const derivedDimensions = DERIVED_COMPARISON_DIMENSION_DEFINITIONS.filter(
        ({ baseDimensionId }) => baseDimensionId === dimension.id
      ).map((derivedDimension) =>
        buildDerivedComparisonDimension({
          basketItemIds,
          dimension: derivedDimension,
          overallReach,
          respondents,
        })
      );

      return [baseDimension, ...derivedDimensions];
    })
    .filter(({ rows }) => rows.length > 0);
};

const buildRawComparisonDimension = ({
  basketItemIds,
  demographicFilters,
  dimension,
  overallReach,
  quotaGroups,
  respondents,
}: {
  basketItemIds: string[];
  demographicFilters: GlobalDemographicFilter[];
  dimension: ComparisonDimensionDefinition;
  overallReach: TurfReachResult;
  quotaGroups: QuotaGroup[];
  respondents: TurfRespondent[];
}): ComparisonDimension => {
  const labelResolver = buildDimensionLabelResolver({
    demographicFilters,
    dimension,
    quotaGroups,
  });
  const rowOrderById = buildDimensionRowOrderById({
    demographicFilters,
    dimension,
    quotaGroups,
  });
  const respondentsByCut = groupRespondentsByRawAccessor(respondents, dimension.accessor);
  const rows = Array.from(respondentsByCut.entries())
    .map(([cutId, cut], sourceIndex) =>
      buildComparisonRow({
        basketItemIds,
        cut,
        id: cutId,
        label: labelResolver(cutId, cut.label),
        overallReach,
        sourceIndex,
      })
    )
    .sort((a, b) => compareRowsByFilterOrder(a, b, rowOrderById))
    .map(({ sourceIndex, ...row }) => row);

  return {
    id: dimension.id,
    label: dimension.label,
    overallReach,
    rows,
  };
};

const buildDerivedComparisonDimension = ({
  basketItemIds,
  dimension,
  overallReach,
  respondents,
}: {
  basketItemIds: string[];
  dimension: DerivedComparisonDimensionDefinition;
  overallReach: TurfReachResult;
  respondents: TurfRespondent[];
}): ComparisonDimension => {
  const respondentsByCut = groupRespondentsByDerivedAccessor(respondents, dimension);
  const rows = dimension.groups
    .map((group, sourceIndex) => {
      const cut = respondentsByCut.get(group.id);

      if (!cut) return undefined;

      return buildComparisonRow({
        basketItemIds,
        cut,
        id: group.id,
        label: group.label,
        overallReach,
        sourceIndex,
      });
    })
    .filter((row): row is SortableComparisonRow => Boolean(row))
    .map(({ sourceIndex, ...row }) => row);

  return {
    id: dimension.id,
    label: dimension.label,
    overallReach,
    rows,
  };
};

const buildComparisonRow = ({
  basketItemIds,
  cut,
  id,
  label,
  overallReach,
  sourceIndex,
}: {
  basketItemIds: string[];
  cut: RespondentCut;
  id: string;
  label: string;
  overallReach: TurfReachResult;
  sourceIndex: number;
}): SortableComparisonRow => {
  const reach = calcReach(cut.respondents, basketItemIds);

  return {
    id,
    label,
    lowN: cut.respondents.length < LOW_N_THRESHOLD,
    n: cut.respondents.length,
    overIndexPct: reach.pct - overallReach.pct,
    reach,
    sourceIndex,
  };
};

const buildDimensionRowOrderById = ({
  demographicFilters,
  dimension,
  quotaGroups,
}: {
  demographicFilters: GlobalDemographicFilter[];
  dimension: ComparisonDimensionDefinition;
  quotaGroups: QuotaGroup[];
}): Map<string, number> => {
  if (dimension.type === 'quota') return buildQuotaGroupOrderByToken(quotaGroups);

  const filter = demographicFilters.find(({ id }) => id === dimension.accessor);
  const orderByToken = new Map<string, number>();

  filter?.options?.forEach((option, index) => {
    [option.id, option.label].forEach((value) => {
      const token = normalizeComparableText(value);

      if (token && !orderByToken.has(token)) orderByToken.set(token, index);
    });
  });

  return orderByToken;
};

const compareRowsByFilterOrder = (
  a: SortableComparisonRow,
  b: SortableComparisonRow,
  rowOrderById: Map<string, number>
): number => {
  const aOrder = rowOrderById.get(a.id);
  const bOrder = rowOrderById.get(b.id);

  if (aOrder != null && bOrder != null) return aOrder - bOrder;
  if (aOrder != null) return -1;
  if (bOrder != null) return 1;

  return a.sourceIndex - b.sourceIndex;
};

export const buildComparisonKeyFindings = ({
  dimensions = [],
  limit = 3,
  minN = MIN_KEY_FINDING_N,
  minOverIndexPct = MIN_KEY_FINDING_OVER_INDEX,
}: BuildKeyFindingsOptions): ComparisonKeyFinding[] =>
  dimensions
    .flatMap((dimension) =>
      dimension.rows.map((row) => ({
        dimensionId: dimension.id,
        dimensionLabel: dimension.label,
        n: row.n,
        overIndexPct: row.overIndexPct,
        reachPct: row.reach.pct,
        rowId: row.id,
        rowLabel: row.label,
      }))
    )
    .filter(({ n, overIndexPct }) => n >= minN && overIndexPct >= minOverIndexPct)
    .sort((a, b) => {
      const overIndexDifference = b.overIndexPct - a.overIndexPct;

      if (overIndexDifference !== 0) return overIndexDifference;

      const reachDifference = b.reachPct - a.reachPct;

      if (reachDifference !== 0) return reachDifference;

      return b.n - a.n;
    })
    .slice(0, limit);

const buildDimensionLabelResolver = ({
  demographicFilters,
  dimension,
  quotaGroups,
}: {
  demographicFilters: GlobalDemographicFilter[];
  dimension: ComparisonDimensionDefinition;
  quotaGroups: QuotaGroup[];
}): ((cutId: string, fallbackLabel: string) => string) => {
  if (dimension.type === 'quota') {
    const quotaGroupLabelByToken = buildQuotaGroupLabelByToken(quotaGroups);

    return (cutId, fallbackLabel) => quotaGroupLabelByToken.get(cutId) ?? fallbackLabel;
  }

  const filter = demographicFilters.find(({ id }) => id === dimension.accessor);
  const optionLabelByToken = new Map<string, string>();

  filter?.options?.forEach((option) => {
    [option.id, option.label].forEach((value) => {
      const token = normalizeComparableText(value);

      if (token) optionLabelByToken.set(token, option.label);
    });
  });

  return (cutId, fallbackLabel) => optionLabelByToken.get(cutId) ?? fallbackLabel;
};

const buildQuotaGroupLabelByToken = (quotaGroups: QuotaGroup[]): Map<string, string> => {
  const labelByToken = new Map<string, string>();

  quotaGroups.forEach((group) => {
    const label = group.name ?? String(group.instantSurveyGroupRef ?? group.id ?? '');
    const tokens = [
      group.id,
      group.instantSurveyGroupRef,
      group.name,
      ...(group.quotaGroupRefs ?? []),
    ];

    tokens.forEach((tokenValue) => {
      const token = normalizeComparableText(tokenValue);

      if (token) labelByToken.set(token, label);
    });
  });

  return labelByToken;
};

const buildQuotaGroupOrderByToken = (quotaGroups: QuotaGroup[]): Map<string, number> => {
  const orderByToken = new Map<string, number>();

  quotaGroups.forEach((group, index) => {
    const tokens = [
      group.id,
      group.instantSurveyGroupRef,
      group.name,
      ...(group.quotaGroupRefs ?? []),
    ];

    tokens.forEach((tokenValue) => {
      const token = normalizeComparableText(tokenValue);

      if (token && !orderByToken.has(token)) orderByToken.set(token, index);
    });
  });

  return orderByToken;
};

const groupRespondentsByRawAccessor = (
  respondents: TurfRespondent[],
  accessor: string
): Map<string, RespondentCut> =>
  respondents.reduce((groups, respondent) => {
    const rawValue = respondent.rawRow[accessor];
    const cutId = normalizeComparableText(rawValue);
    const fallbackLabel = String(rawValue ?? '').trim();

    if (!cutId) return groups;

    const group = groups.get(cutId) ?? {
      label: fallbackLabel,
      respondents: [],
    };
    group.respondents.push(respondent);
    groups.set(cutId, group);

    return groups;
  }, new Map<string, RespondentCut>());

const groupRespondentsByDerivedAccessor = (
  respondents: TurfRespondent[],
  dimension: DerivedComparisonDimensionDefinition
): Map<string, RespondentCut> =>
  respondents.reduce((groups, respondent) => {
    const groupDefinition = dimension.resolveGroup(respondent.rawRow[dimension.accessor]);

    if (!groupDefinition) return groups;

    const group = groups.get(groupDefinition.id) ?? {
      label: groupDefinition.label,
      respondents: [],
    };
    group.respondents.push(respondent);
    groups.set(groupDefinition.id, group);

    return groups;
  }, new Map<string, RespondentCut>());

function resolveAgeGroup(value: unknown): DerivedComparisonGroupDefinition | undefined {
  const age = getFirstNumericValue(value);

  if (age == null) return undefined;
  if (age >= 18 && age <= 34) return AGE_GROUPS[0];
  if (age >= 35 && age <= 54) return AGE_GROUPS[1];
  if (age >= 55) return AGE_GROUPS[2];

  return undefined;
}

function resolveIncomeGroup(value: unknown): DerivedComparisonGroupDefinition | undefined {
  const normalizedValue = normalizeComparableText(value);
  const income = getFirstNumericValue(normalizedValue);

  if (!normalizedValue || income == null) return undefined;
  if (normalizedValue.includes('under') || normalizedValue.includes('less than')) {
    return INCOME_GROUPS[0];
  }
  if (income < 40) return INCOME_GROUPS[0];
  if (income < 125) return INCOME_GROUPS[1];

  return INCOME_GROUPS[2];
}

const getFirstNumericValue = (value: unknown): number | undefined => {
  const normalizedValue = normalizeComparableText(value).replace(/,/g, '');
  const match = normalizedValue.match(/\d+/);

  if (!match) return undefined;

  const parsedValue = Number(match[0]);

  if (!Number.isFinite(parsedValue)) return undefined;

  return parsedValue >= 1000 ? parsedValue / 1000 : parsedValue;
};
